/** @file ProjectsScreen: project (game) cards with puppet counts, New project, a Trash tab, card menus. */
import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { useApi } from '../shell/useApi';
import type { EditorStore } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import type { EditorApi, ProjectEntry } from '../store/editorApi';
import { Card } from '../ui-kit/Card';
import { Spinner } from '../ui-kit/Spinner';
import { Tabs } from '../ui-kit/Tabs';
import { entryCardMenu } from './entryCardMenu';
import { NewProjectBar } from './NewProjectBar';
import { runAndReload } from './runAndReload';
import { useServerList } from './useServerList';

type Tab = 'projects' | 'trash';

const TABS: { id: Tab; label: string }[] = [{ id: 'projects', label: 'Projects' }, { id: 'trash', label: 'Trash' }];

/** The menu of one project card, wired to the api. */
function cardMenu(store: EditorStore, api: EditorApi, p: ProjectEntry, trashed: boolean, reload: () => Promise<void>) {
  return entryCardMenu(trashed, {
    open: () => { store.getState().navigate({ screen: 'puppets', projectId: p.id }); },
    rename: () => {
      const name = globalThis.prompt('Rename project', p.name)?.trim();
      if (name === undefined || name === '' || name === p.name) return;
      runAndReload(store, () => api.projects.rename(p.id, name), reload)();
    },
    trash: runAndReload(store, () => api.projects.trash(p.id), reload),
    restore: runAndReload(store, () => api.projects.restore(p.id), reload),
    purge: () => {
      if (globalThis.confirm(`Purge "${p.name}" and its puppets for good? This cannot be undone.`)) runAndReload(store, () => api.projects.purge(p.id), reload)();
    },
  });
}

const when = (at: number): string => new Date(at).toLocaleString();

const puppetCount = (n: number): string => `${String(n)} puppet${n === 1 ? '' : 's'}`;

/** Without a server (the memory store dev page) the screen offers only the project of the puppet in memory. */
function MemoryProjects(): ReactElement {
  const store = useEditorStore();
  const id = useEditor((s) => s.puppet.projectId);
  return (
    <div className="p3-screen">
      <div className="p3-screen-head"><h1>Projects</h1><span className="p3-muted">no server: memory store</span></div>
      {id === null ? null : (
        <div className="p3-grid">
          <Card title={id} subtitle="in memory · 1 puppet" onOpen={() => { store.getState().navigate({ screen: 'puppets', projectId: id }); }} />
        </div>
      )}
    </div>
  );
}

/** The server's projects (live or trashed), newest first; click opens a project's puppets, right-click its menu. */
function ServerProjects({ api }: { api: EditorApi }): ReactElement {
  const store = useEditorStore();
  const [tab, setTab] = useState<Tab>('projects');
  const trashed = tab === 'trash';
  const list = useServerList(useCallback(() => api.projects.list(trashed ? { trashed: true } : {}), [api, trashed]));
  const open = (id: string) => (): void => { store.getState().navigate({ screen: 'puppets', projectId: id }); };
  return (
    <div className="p3-screen">
      <div className="p3-screen-head"><h1>Projects</h1><Tabs label="Project lists" tabs={TABS} active={tab} onSelect={setTab} /></div>
      {trashed ? null : <div className="p3-stack" style={{ marginBottom: 16 }}><NewProjectBar api={api} /></div>}
      {list.error === null ? null : <p className="p3-error" role="alert">{list.error}</p>}
      {list.entries === null ? <Spinner /> : null}
      {list.entries?.length === 0 ? <p className="p3-muted">{trashed ? 'The trash is empty.' : 'No projects yet: a project is a game that holds its puppets.'}</p> : null}
      <div className="p3-grid">
        {(list.entries ?? []).map((p) => (
          <Card key={p.id} title={p.name} subtitle={`${puppetCount(p.puppetCount)} · ${when(p.trashedAt ?? p.updatedAt)}`}
            onOpen={trashed ? undefined : open(p.id)}
            onMenu={(x, y) => { store.getState().openMenu(x, y, cardMenu(store, api, p, trashed, list.reload)); }} />
        ))}
      </div>
    </div>
  );
}

/** The projects (games) of the server the api talks to, or the memory puppet's project without one. */
export function ProjectsScreen(): ReactElement {
  const api = useApi();
  return api === null ? <MemoryProjects /> : <ServerProjects api={api} />;
}
